import { useParams, Navigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { PRODUCT_PAGES } from "@/data/products";

export default function ProductPage() {
  const { id } = useParams();
  const p = PRODUCT_PAGES[id];
  if (!p) return <Navigate to="/" replace />;

  const Icon = p.icon;

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center animate-fade-up">
          <div className="md:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-coral mb-6">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-coral/10">
                <Icon size={12} strokeWidth={1.75} className="text-coral" />
              </span>
              {p.eyebrow}
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl leading-[1.05] text-ink-900 font-light">
              {p.title}
            </h1>
            <p className="mt-6 text-lg text-ink-500 max-w-xl leading-relaxed">
              {p.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to={p.cta_route}>
                <Button
                  size="lg"
                  className="rounded-full bg-coral hover:bg-coral-hover text-white px-7 h-12"
                  data-testid="product-cta-primary"
                >
                  Crear gratis
                  <ArrowRight size={18} strokeWidth={1.75} className="ml-2" />
                </Button>
              </Link>
              <Link to="/">
                <Button
                  variant="ghost"
                  size="lg"
                  className="rounded-full h-12 text-ink-700 hover:bg-cream-100 px-6"
                >
                  Ver todos los productos
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-ink-500">Gratis para empezar. Sin tarjeta.</p>
          </div>

          <div className="md:col-span-5">
            <img
              src={p.hero_img}
              alt={p.eyebrow}
              className="w-full h-[380px] object-cover rounded-3xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-cream-100 border-y border-line py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.24em] text-coral">
              Cómo funciona
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl text-ink-900 font-light leading-tight">
              Lo que incluye desde el primer minuto.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {p.features.map((f, i) => (
              <div
                key={i}
                className="bg-white border border-line rounded-2xl p-6 transition-transform duration-200 ease-out hover:-translate-y-1"
                data-testid={`feat-${i}`}
              >
                <div className="w-9 h-9 rounded-full bg-coral/10 flex items-center justify-center text-coral">
                  <Check size={16} strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 font-heading text-xl text-ink-900">{f.title}</h3>
                <p className="mt-2 text-ink-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases chips */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.24em] text-coral">
            Ideal para
          </span>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl text-ink-900 font-light leading-tight">
            Escenarios donde brilla.
          </h2>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          {p.use_cases.map((uc) => (
            <span
              key={uc}
              className="bg-white border border-line rounded-full px-5 py-2 text-ink-700"
              data-testid={`use-case-chip-${uc}`}
            >
              {uc}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        <div className="rounded-3xl bg-ink-900 text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
          <h2 className="font-heading text-3xl sm:text-4xl font-light leading-tight relative">
            Empieza ahora — <em className="text-coral not-italic">gratis y sin tarjeta.</em>
          </h2>
          <p className="mt-4 text-ink-300 max-w-2xl relative">
            Crea tu primer evento en menos de un minuto.
          </p>
          <div className="mt-6 relative">
            <Link to={p.cta_route}>
              <Button
                size="lg"
                className="rounded-full bg-coral hover:bg-coral-hover text-white px-7 h-12"
                data-testid="product-cta-bottom"
              >
                Crear evento gratis
                <ArrowRight size={18} strokeWidth={1.75} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-line bg-cream-100 py-8 text-center text-sm text-ink-500">
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-coral text-white">
            <Sparkles size={12} strokeWidth={1.75} />
          </span>
          <span className="font-heading text-ink-900">Apúntate</span>
        </div>
        Hecho con cariño en México
      </footer>
    </div>
  );
}
